import React, { useCallback, useEffect, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import { visibleTimeline } from "../shared/intelligenceLog";
import { parseDecisionMakers } from "../shared/enums";
import { parseSocials } from './WorldDbTableRow';
import CompanyRecord from './CompanyRecord';
import GoogleReviewsInsight from './GoogleReviewsInsight';
import WebsiteAnalyzerInsight from './WebsiteAnalyzerInsight';
import ConnectorDataInsight from './ConnectorDataInsight';
import ContactMediaInsight from './ContactMediaInsight';
import { buildContactActivity, sentMailDocId } from './contactActivity';
import useContactMailHistory from './tasks/useContactMailHistory';
import RecordedMail, { sentEmailFromRecord } from './RecordedMail';
import './IntelligenceModal.css';

const SIZE_LABELS = { SOLO: 'Solo', SMALL: 'Small (2–10)', MID: 'Mid (10–200)', ENT: 'Enterprise (200+)' };

const PROVENANCE_LABELS = {
  human: 'Manual', research: 'Web Research', research_dm: 'Web Research',
  rules: 'Auto-Analysis', nano_llm: 'AI Analysis', llm: 'AI Analysis',
};
const PROVENANCE_ICONS = {
  human: 'fa-user-check', research: 'fa-globe', research_dm: 'fa-globe',
  rules: 'fa-magic', nano_llm: 'fa-brain', llm: 'fa-brain',
};

const REPLY_LABELS = {
  interested: 'Interested', price_objection: 'Price Objection', no_need: 'No Need',
  later: 'Later', wrong_person: 'Wrong Person', hostile: 'Hostile',
  spam_accusation: 'Spam Report', neutral: 'Neutral',
};
const REPLY_COLORS = {
  interested: '#22c55e', price_objection: '#f59e0b', no_need: '#94a3b8',
  later: '#3b82f6', wrong_person: '#a855f7', hostile: '#ef4444',
  spam_accusation: '#ef4444', neutral: '#94a3b8',
};

const RATING_CONFIG = {
  High: { color: '#22c55e', bg: '#f0fdf4', border: '#dcfce7', icon: 'fa-arrow-up', label: 'High Potential' },
  Medium: { color: '#f59e0b', bg: '#fffbeb', border: '#fef3c7', icon: 'fa-minus', label: 'Medium Potential' },
  Low: { color: '#94a3b8', bg: '#f8fafc', border: '#f1f5f9', icon: 'fa-arrow-down', label: 'Low Potential' },
};

const NEWEST_SIGNAL_TYPES = {
  recent: { label: 'Recent', icon: 'fa-clock' },
  unique: { label: 'Stands out', icon: 'fa-star' },
  opportunity: { label: 'Opportunity', icon: 'fa-bullseye' },
};

const formatShortDate = (value) => {
  const t = Date.parse(value || '');
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Research data comes from LLMs; only plain http(s) links are rendered.
const safeUrl = (u) => (typeof u === 'string' && /^https?:\/\/[^\s]+$/i.test(u.trim()) ? u.trim() : null);

function SourceLink({ url, label }) {
  return (
    <a className="ir-source-link" href={url} target="_blank" rel="noopener noreferrer" title={url}>
      <i className="fa fa-arrow-up-right-from-square" />{label || displayHost(url).split('/')[0]}
    </a>
  );
}

function ProvenanceBadge({ source }) {
  if (!source) return null;
  const label = PROVENANCE_LABELS[source] || source;
  const icon = PROVENANCE_ICONS[source] || 'fa-question';
  return (
    <span className="ir-prov" title={`Source: ${label}`}>
      <i className={`fa ${icon}`} />{label}
    </span>
  );
}

function ScoreMeter({ label, value, max = 100, format, color, icon }) {
  const pct = value != null ? Math.min((value / max) * 100, 100) : 0;
  const displayVal = value != null ? (format ? format(value) : value) : '–';
  const barColor = color || (pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#94a3b8');
  return (
    <div className="ir-meter">
      {icon && <i className={`fa ${icon} ir-meter-icon`} />}
      <span className="ir-meter-label">{label}</span>
      <div className="ir-meter-track">
        <div className="ir-meter-fill" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <span className="ir-meter-val">{displayVal}</span>
    </div>
  );
}

const parseObject = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch (_) { return null; }
  }
  return null;
};

// LLM research fields are usually strings but sometimes arrive as objects
// (e.g. location: { city, country }), which React cannot render.
const asText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(', ');
  if (typeof v === 'object') return Object.values(v).map(asText).filter(Boolean).join(', ');
  return String(v);
};

const ABBREVIATION_END = /\b(Co|Dr|Nr|St|Str|bzw|ca|Inc|Ltd|Mio|Mrd|Prof|Hr|Fr|z\.B|u\.a|d\.h|e\.V|ggf|inkl|evtl|usw|etc)\.$/i;

// Company descriptions come back as one long block. Break them into short
// paragraphs so the research reads like text, not a wall.
const toParagraphs = (raw, sentencesPerParagraph = 2) => {
  const text = asText(raw).trim();
  if (!text) return [];
  const explicit = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (explicit.length > 1) return explicit;
  const sentences = [];
  text.split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ0-9„"])/).forEach((part) => {
    const last = sentences[sentences.length - 1];
    if (last && ABBREVIATION_END.test(last)) sentences[sentences.length - 1] = `${last} ${part}`;
    else sentences.push(part);
  });
  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += sentencesPerParagraph) {
    paragraphs.push(sentences.slice(i, i + sentencesPerParagraph).join(' '));
  }
  return paragraphs;
};

const displayHost = (url) => String(url || '')
  .replace(/^https?:\/\//i, '')
  .replace(/^www\./i, '')
  .replace(/\/+$/, '');

const toHref = (url) => {
  const v = String(url || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const initials = (name) => String(name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('') || '?';

const LEGAL_WORD = /^(gmbh|ag|ug|kg|ohg|gbr|se|ltd|limited|inc|llc|bv|nv|sa|sarl|srl|plc|mbh|ggmbh|ek|haftungsbeschränkt|haftungsbeschraenkt)$/i;

const splitCompanyTitle = (raw) => {
  const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return { name: parts.join(' ') || String(raw || ''), legal: '' };
  let cut = parts.length;
  while (cut > 1) {
    const token = parts[cut - 1].replace(/[().,]/g, '').toLowerCase();
    if (token === '&' || token === 'und' || token === 'and' || token === 'co' || LEGAL_WORD.test(token)) {
      cut -= 1;
      continue;
    }
    break;
  }
  const tail = parts.slice(cut);
  if (!tail.length || cut === 0 || !tail.some((part) => LEGAL_WORD.test(part.replace(/[().,]/g, '')))) {
    return { name: parts.join(' '), legal: '' };
  }
  return { name: parts.slice(0, cut).join(' '), legal: tail.join(' ') };
};

const IntelligenceModal = ({ row, userId, onClose }) => {
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [scoresOpen, setScoresOpen] = useState(false);
  const [log, setLog] = useState([]);
  const [logStatus, setLogStatus] = useState('idle');
  const [manualOpenId, setManualOpenId] = useState(undefined);
  const [listSource, setListSource] = useState(null);
  const [mailView, setMailView] = useState(null);
  const mailViewRef = useRef(null);
  mailViewRef.current = mailView;
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // The modal stays mounted and only returns null while closed, so `closing`
  // would otherwise stick and the next open would play the slide-down animation.
  const rowId = row?.id || null;
  const [sheetSession, setSheetSession] = useState(rowId);
  if (rowId !== sheetSession) {
    setSheetSession(rowId);
    setClosing(false);
    closingRef.current = false;
  }

  const finishClose = useCallback(() => {
    onCloseRef.current?.();
  }, []);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    const reduce = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      closingRef.current = true;
      finishClose();
      return;
    }
    closingRef.current = true;
    setClosing(true);
  }, [finishClose]);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(finishClose, 420);
    return () => window.clearTimeout(timer);
  }, [closing, finishClose]);

  useEffect(() => {
    if (!rowId) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (mailViewRef.current) {
        setMailView(null);
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [rowId, requestClose]);

  useEffect(() => {
    setManualOpenId(undefined);
    setMailView(null);
  }, [row?.id]);

  useEffect(() => {
    if (!userId || !row?.id) {
      setLog([]);
      setLogStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setLog([]);
    setLogStatus('loading');
    getDocs(query(
      collection(firestore, `World/${userId}/db/${row.id}/intelligenceLog`),
      orderBy('capturedAt', 'desc'),
      limit(50),
    )).then((snap) => {
      if (cancelled) return;
      setLog(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLogStatus('ready');
    }).catch(() => {
      if (!cancelled) setLogStatus('error');
    });
    return () => { cancelled = true; };
  }, [userId, row?.id]);

  const [logEntries, setLogEntries] = useState([]);
  useEffect(() => {
    setLogEntries([]);
    if (!userId || !row?.id) return undefined;
    let cancelled = false;
    getDocs(query(
      collection(firestore, `World/${userId}/db/${row.id}/activityLog`),
      orderBy('at', 'desc'),
      limit(100),
    )).then((snap) => {
      if (!cancelled) setLogEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, row?.id]);

  const importName = row?.import_name || '';
  useEffect(() => {
    setListSource(null);
    if (!userId || !importName || importName.includes('/')) return undefined;
    let cancelled = false;
    getDoc(doc(firestore, `World/${userId}/import_names`, importName))
      .then((snap) => { if (!cancelled && snap.exists()) setListSource(snap.data().source || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId, importName]);

  const mailAddresses = row ? [
    row.lead_email, row.email,
    ...parseDecisionMakers(row).map((dm) => dm && dm.email),
  ].filter((e) => typeof e === 'string' && e.includes('@')) : [];
  const { mails, loading: mailsLoading } = useContactMailHistory(userId, row?.id, mailAddresses);

  useEffect(() => {
    if (!mailView || mailView.status === 'missing' || mailView.status === 'error') return undefined;
    const item = mailView.item;
    const showStored = (mail) => {
      const next = sentEmailFromRecord(mail, item || {});
      const current = mailView.email;
      if (current && current.content === next.content && current.from === next.from && current.to === next.to) {
        return;
      }
      setMailView({ status: 'ready', item, email: next });
    };
    if (!mailsLoading) {
      const id = sentMailDocId(item, mails);
      const cached = id && mails.find((m) => m.dir === 'out' && m.docId === id && (m.html || m.text));
      if (cached) {
        showStored(cached);
        return undefined;
      }
      if (item && (item.html || item.text)) {
        if (mailView.status === 'loading') {
          showStored({
            docId: item.mailDocId || '',
            subject: item.subject || '',
            from: item.from || '',
            address: item.address || '',
            cc: item.cc || [],
            html: item.html || '',
            text: item.text || '',
          });
        }
        return undefined;
      }
      if (mailView.status !== 'loading') return undefined;
      if (!userId || !id) {
        setMailView({ status: 'missing', item, email: null });
        return undefined;
      }
      let cancelled = false;
      getDoc(doc(firestore, 'world_sent', userId, 'emails', id)).then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setMailView({ status: 'missing', item, email: null });
          return;
        }
        const data = snap.data() || {};
        setMailView({
          status: 'ready',
          item,
          email: sentEmailFromRecord({
            ...data,
            docId: snap.id,
            html: data.content || '',
            text: data.text || '',
            address: data.to || '',
          }),
        });
      }).catch(() => {
        if (!cancelled) setMailView({ status: 'error', item, email: null });
      });
      return () => { cancelled = true; };
    }
    if (mailView.status === 'loading' && item && (item.html || item.text)) {
      showStored({
        docId: item.mailDocId || '',
        subject: item.subject || '',
        from: item.from || '',
        address: item.address || '',
        cc: item.cc || [],
        html: item.html || '',
        text: item.text || '',
      });
    }
    return undefined;
  }, [mailView, mails, mailsLoading, userId]);

  if (!row) return null;

  const activity = buildContactActivity(row, { mails, logEntries, listSource });

  const signals = row.leadSignals || {};
  const ri = row.researchIntelligence || {};
  const prov = signals.signalProvenance || {};
  const tags = signals.tags || [];
  const enthusiasm = row.lead_happyness ? parseInt(row.lead_happyness) : null;
  const spyConf = row.spy_confidence_score ? parseInt(row.spy_confidence_score) : null;
  const leadScore = row.lead_score ? parseInt(row.lead_score) : null;
  const hasReply = row.lead_reply === true || row.lead_reply === 'true';
  const contactName = row.business_name || row.name || row.first_name || 'Unknown';
  const { name: titleName, legal: titleLegal } = splitCompanyTitle(contactName);
  const website = row.lead_website || (row.email ? row.email.split('@')[1] : '') || '';
  const contactEmail = row.lead_email || row.email || '';
  const contactPhone = row.lead_telephone || row.phone || '';
  const contactPlace = [row.lead_postcode, row.lead_city].filter(Boolean).join(' ');
  const replySubtype = row.lead_reply_subtype || null;

  const ratingCfg = ri.leadRating ? (RATING_CONFIG[ri.leadRating] || RATING_CONFIG.Medium) : null;
  const dmRoles = Array.isArray(ri.decisionMakerRoles) ? ri.decisionMakerRoles : [];
  const decisionMakers = parseDecisionMakers(row).filter((dm) => dm && (dm.name || dm.fullName || dm.email || dm.phone));
  const hasResearch = ri.version != null || ri.industryFit || ri.companyInfo || ri.researchedAt
    || (Array.isArray(ri.keyFacts) && ri.keyFacts.length > 0)
    || (Array.isArray(ri.latestNews) && ri.latestNews.length > 0)
    || !!ri.websiteFacts;
  const potential = parseObject(row.potential_analysis);
  const potentialScore = typeof row.potential_score === 'number'
    ? row.potential_score
    : (typeof potential?.score === 'number' ? potential.score : null);
  const potentialBullets = Array.isArray(potential?.bullets)
    ? potential.bullets.filter((b) => typeof b === 'string' && b.trim())
    : [];
  const hasPotential = potentialScore != null || potential?.summary || potential?.fitLabel || potentialBullets.length > 0;
  const quickStats = ri.quickStats && typeof ri.quickStats === 'object' ? ri.quickStats : {};
  const recordEvents = visibleTimeline(log, row);
  const autoOpenId = recordEvents.find((event) => (
    (event.changes && event.changes.length)
    || (event.addedNotes && event.addedNotes.length)
    || (event.removedNotes && event.removedNotes.length)
  ))?.id || null;
  const openId = manualOpenId === undefined ? autoOpenId : manualOpenId;
  const u = (v) => v && v !== 'unknown';
  const hasSignals = u(signals.industry) || u(signals.b2b_b2c) || u(signals.sizeProxy) || u(signals.offerType) || (tags && tags.length > 0);
  const igInsight = ri.instagramInsight || null;
  const seo = ri.seoAnalysis || null;
  const newestSignals = Array.isArray(ri.newestSignals) ? ri.newestSignals.filter((s) => s && s.title).slice(0, 3) : [];

  const signalFields = [
    signals.industry && signals.industry !== 'unknown' && {
      label: 'Industry', value: signals.industry, prov: prov.industry,
    },
    signals.b2b_b2c && signals.b2b_b2c !== 'unknown' && {
      label: 'Target Market', value: signals.b2b_b2c.toUpperCase(), prov: prov.b2b_b2c,
    },
    signals.sizeProxy && signals.sizeProxy !== 'unknown' && {
      label: 'Company Size', value: SIZE_LABELS[signals.sizeProxy] || signals.sizeProxy, prov: prov.sizeProxy,
    },
    signals.offerType && signals.offerType !== 'unknown' && {
      label: 'Offer Type', value: signals.offerType, prov: prov.offerType,
    },
    signals.language && { label: 'Language', value: signals.language },
    signals.geoHint && {
      label: 'Region',
      value: typeof signals.geoHint === 'object'
        ? [signals.geoHint.city, signals.geoHint.region !== signals.geoHint.city && signals.geoHint.region, signals.geoHint.country]
          .filter(Boolean).join(', ')
        : signals.geoHint,
    },
  ].filter(Boolean);

  const socials = parseSocials(row);
  const buildHref = (platform, val) => {
    if (!val || !val.trim()) return val;
    const v = val.trim();
    if (v.startsWith('http')) return v;
    const domains = { linkedin: 'linkedin.com/in', facebook: 'facebook.com', instagram: 'instagram.com', x: 'x.com', tiktok: 'tiktok.com/@', youtube: 'youtube.com/@', xing: 'xing.com/profile' };
    return domains[platform] ? `https://${domains[platform]}/${v.replace('@', '')}` : `https://${v}`;
  };
  const socialLinks = [
    socials.linkedin && { icon: 'fa-linkedin', url: buildHref('linkedin', socials.linkedin), label: 'LinkedIn' },
    socials.instagram && { icon: 'fa-instagram', url: buildHref('instagram', socials.instagram), label: 'Instagram' },
    socials.facebook && { icon: 'fa-facebook', url: buildHref('facebook', socials.facebook), label: 'Facebook' },
    socials.x && { icon: 'fa-x-twitter', url: buildHref('x', socials.x), label: 'X' },
    socials.tiktok && { icon: 'fa-tiktok', url: buildHref('tiktok', socials.tiktok), label: 'TikTok' },
    socials.youtube && { icon: 'fa-youtube', url: buildHref('youtube', socials.youtube), label: 'YouTube' },
    socials.xing && { icon: 'fa-xing', url: buildHref('xing', socials.xing), label: 'Xing' },
  ].filter(Boolean);

  const sourceQuality = signals.sourceQuality || null;

  const researchFacts = [
    quickStats.employees && { icon: 'fa-users', label: 'Employees', value: asText(quickStats.employees) },
    quickStats.revenue && { icon: 'fa-euro-sign', label: 'Revenue', value: asText(quickStats.revenue) },
    quickStats.founded && { icon: 'fa-calendar', label: 'Founded', value: asText(quickStats.founded) },
    quickStats.location && { icon: 'fa-location-dot', label: 'Headquarters', value: asText(quickStats.location) },
    ri.companySizeEstimate && { icon: 'fa-building', label: 'Est. Size', value: asText(ri.companySizeEstimate) },
  ].filter(Boolean);
  const aboutParagraphs = toParagraphs(ri.companyInfo);
  const keyFacts = Array.isArray(ri.keyFacts) ? ri.keyFacts.map(asText).filter(Boolean) : [];
  const buySignals = Array.isArray(ri.buySignals)
    ? ri.buySignals
      .map((s) => (typeof s === 'string'
        ? { text: asText(s), url: null }
        : { text: asText(s?.signal), url: safeUrl(s?.sourceUrl) }))
      .filter((s) => s.text)
    : [];
  const news = Array.isArray(ri.latestNews)
    ? ri.latestNews
      .map((item) => (typeof item === 'string'
        ? { headline: item, date: '', url: null }
        : { headline: asText(item?.headline), date: asText(item?.date), url: safeUrl(item?.sourceUrl) }))
      .filter((item) => item.headline)
    : [];

  const siteFacts = ri.websiteFacts && typeof ri.websiteFacts === 'object' ? ri.websiteFacts : null;
  const imprint = siteFacts?.imprint || null;
  const imprintPage = (siteFacts?.pagesFetched || []).find((p) => p.type === 'imprint')?.url || null;
  const registerRows = imprint ? [
    imprint.managers?.length > 0 && {
      label: 'Management',
      value: imprint.managers.map((m) => `${asText(m.name)}${m.role ? ` (${asText(m.role)})` : ''}`).join(', '),
    },
    imprint.registerId && { label: 'Register', value: [imprint.registerId, imprint.registerCourt].filter(Boolean).map(asText).join(', ') },
    imprint.vatId && { label: 'VAT ID', value: asText(imprint.vatId) },
    imprint.address && { label: 'Address', value: asText(imprint.address) },
  ].filter(Boolean) : [];
  const siteChanges = siteFacts?.changes || null;
  const changeLines = siteChanges ? [
    ...(siteChanges.newPages || []).slice(0, 5).map((p) => ({ kind: 'New page', text: asText(p.title || displayHost(p.url)), url: safeUrl(p.url) })),
    ...(siteChanges.changedPages || []).slice(0, 3).map((p) => ({ kind: 'Updated', text: `${asText(p.type)} page`, url: safeUrl(p.url) })),
    ...(siteChanges.newJobs || []).slice(0, 4).map((j) => ({ kind: 'New job', text: asText(j), url: null })),
    ...(siteChanges.removedJobs || []).slice(0, 3).map((j) => ({ kind: 'Job closed', text: asText(j), url: null })),
  ] : [];
  const openJobs = Array.isArray(siteFacts?.jobs) ? siteFacts.jobs.map(asText).filter(Boolean).slice(0, 8) : [];

  return (
    <div className="ir-overlay" onClick={requestClose}>
      <div
        className={`ir-modal${closing ? ' ir-sheet--out' : ''}`}
        onClick={e => e.stopPropagation()}
        onAnimationEnd={(event) => {
          if (event.target !== event.currentTarget || !closingRef.current) return;
          if (event.animationName !== 'irSheetOut') return;
          finishClose();
        }}
      >

        {/* ── Header: company identity + links ── */}
        <div className="ir-header">
          <div className="ir-header-top">
            <div className="ir-header-info">
              <h3 className="ir-title">
                {titleName}{titleLegal && <> <span className="ir-title-legal">{titleLegal}</span></>}
              </h3>
              {(website || contactEmail || contactPhone || contactPlace || socialLinks.length > 0) && (
                <div className="ir-links">
                  {website && (
                    <a className="ir-link" href={toHref(website)} target="_blank" rel="noopener noreferrer">
                      <i className="fa fa-globe" /><span className="ir-link-label">{displayHost(website)}</span>
                    </a>
                  )}
                  {contactEmail && (
                    <a className="ir-link" href={`mailto:${contactEmail}`}>
                      <i className="fa fa-envelope" /><span className="ir-link-label">{contactEmail}</span>
                    </a>
                  )}
                  {contactPhone && (
                    <a className="ir-link" href={`tel:${contactPhone}`}>
                      <i className="fa fa-phone" /><span className="ir-link-label ir-link-label--num">{contactPhone}</span>
                    </a>
                  )}
                  {contactPlace && (
                    <span className="ir-link">
                      <i className="fa fa-location-dot" /><span className="ir-link-label">{contactPlace}</span>
                    </span>
                  )}
                  {socialLinks.length > 0 && (
                    <div className="ir-socials">
                      {socialLinks.map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="ir-social-link" title={s.label}>
                          <i className={`fab ${s.icon}`} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button className="ir-close" onClick={requestClose} aria-label="Close"><i className="fa fa-times" /></button>
          </div>
          {ratingCfg && (
            <div className="ir-rating-strip" style={{ background: ratingCfg.bg, borderColor: ratingCfg.border }}>
              <i className={`fa ${ratingCfg.icon}`} style={{ color: ratingCfg.color }} />
              <span style={{ color: ratingCfg.color }}>{ratingCfg.label}</span>
              {ri.leadScore != null && (
                <span className="ir-rating-score" style={{ color: ratingCfg.color }}>Score: {ri.leadScore}/100</span>
              )}
            </div>
          )}
        </div>

        <div className="ir-body">
          <div className="ir-main">

            {/* ── Decision Makers ── */}
            {(decisionMakers.length > 0 || dmRoles.length > 0) && (
              <section className="ir-section">
                <div className="ir-section-label">
                  <i className="fa fa-user-tie" />Decision Makers
                  <span className="ir-section-count">{decisionMakers.length || ri.decisionMakerCount || dmRoles.length}</span>
                </div>
                {decisionMakers.length > 0 ? (
                  <div className="ir-dm-grid">
                    {decisionMakers.map((dm, i) => {
                      const name = asText(dm.name || dm.fullName) || 'Unknown';
                      const role = asText(dm.role || dm.position);
                      const phone = asText(dm.phone || dm.telephone);
                      const linkedin = typeof dm.linkedin === 'string' ? dm.linkedin
                        : (dm.socials && typeof dm.socials.linkedin === 'string' ? dm.socials.linkedin : '');
                      return (
                        <div key={i} className="ir-dm-card">
                          <div className="ir-dm-head">
                            <span className="ir-dm-avatar">{initials(name)}</span>
                            <div className="ir-dm-id">
                              <span className="ir-dm-name">{name}</span>
                              {role && <span className="ir-dm-role">{role}</span>}
                            </div>
                            {dm.contacted && <span className="ir-dm-badge">Contacted</span>}
                          </div>
                          <div className="ir-dm-contacts">
                            {dm.email && (
                              <a
                                href={`mailto:${dm.email}`}
                                className={`ir-dm-line ${dm.emailValidationFailed ? 'ir-dm-line--bad' : ''}`}
                                title={dm.emailValidationFailed ? (dm.emailNote || 'Invalid email') : dm.email}
                              >
                                <i className="fa fa-envelope" />{dm.email}
                                {dm.emailVerified && <i className="fa fa-circle-check ir-dm-verified" title="Verified" />}
                              </a>
                            )}
                            {phone && (
                              <a href={`tel:${phone}`} className="ir-dm-line"><i className="fa fa-phone" />{phone}</a>
                            )}
                            {linkedin && (
                              <a href={toHref(linkedin)} target="_blank" rel="noopener noreferrer" className="ir-dm-line">
                                <i className="fab fa-linkedin" />LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ir-dm-roles">
                    {dmRoles.map((role, i) => (
                      <span key={i} className="ir-dm-chip">
                        <i className="fa fa-user" />{asText(role)}
                      </span>
                    ))}
                  </div>
                )}
              </section>
            )}

            <CompanyRecord
              key={row.id}
              events={recordEvents}
              activity={activity}
              status={logStatus}
              openId={openId}
              onToggle={(id) => setManualOpenId(openId === id ? null : id)}
              onOpenMail={(item) => setMailView({ status: 'loading', item, email: null })}
            />

            {/* ── Web Research ── */}
            {hasResearch && (
              <section className="ir-section">
                <div className="ir-section-label"><i className="fa fa-microscope" />Web Research</div>

                {(researchFacts.length > 0 || ri.onlinePresenceScore != null) && (
                  <div className="ir-fact-grid">
                    {researchFacts.map((f) => (
                      <div key={f.label} className="ir-fact">
                        <span className="ir-fact-label"><i className={`fa ${f.icon}`} />{f.label}</span>
                        <span className="ir-fact-value">{f.value}</span>
                      </div>
                    ))}
                    {ri.onlinePresenceScore != null && (
                      <div className="ir-fact">
                        <span className="ir-fact-label"><i className="fa fa-signal" />Online Presence</span>
                        <span className="ir-fact-value">{ri.onlinePresenceScore}/100</span>
                        <div className="ir-mini-meter">
                          <div className="ir-mini-fill" style={{
                            width: `${ri.onlinePresenceScore}%`,
                            background: ri.onlinePresenceScore >= 70 ? '#22c55e' : ri.onlinePresenceScore >= 40 ? '#f59e0b' : '#94a3b8',
                          }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {aboutParagraphs.length > 0 && (
                  <div className="ir-block">
                    <div className="ir-block-title">About the company</div>
                    <div className="ir-about">
                      {aboutParagraphs.map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  </div>
                )}

                {(ri.industryFit || ri.budgetIndicators) && (
                  <div className="ir-insight-grid">
                    {ri.industryFit && (
                      <div className="ir-block">
                        <div className="ir-block-title"><i className="fa fa-bullseye" />Industry fit</div>
                        <p className="ir-block-text">{asText(ri.industryFit)}</p>
                      </div>
                    )}
                    {ri.budgetIndicators && (
                      <div className="ir-block">
                        <div className="ir-block-title"><i className="fa fa-wallet" />Budget indicators</div>
                        <p className="ir-block-text">{asText(ri.budgetIndicators)}</p>
                      </div>
                    )}
                  </div>
                )}

                {(keyFacts.length > 0 || buySignals.length > 0 || news.length > 0) && (
                  <div className="ir-insight-grid">
                    {keyFacts.length > 0 && (
                      <div className="ir-block">
                        <div className="ir-block-title"><i className="fa fa-list-check" />Key facts</div>
                        <ul className="ir-bullets">
                          {keyFacts.map((fact, i) => <li key={i}>{fact}</li>)}
                        </ul>
                      </div>
                    )}
                    {buySignals.length > 0 && (
                      <div className="ir-block">
                        <div className="ir-block-title"><i className="fa fa-bolt" />Buy signals</div>
                        <ul className="ir-bullets">
                          {buySignals.map((s, i) => (
                            <li key={i}>
                              {s.text}
                              {s.url && <SourceLink url={s.url} />}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {news.length > 0 && (
                      <div className="ir-block">
                        <div className="ir-block-title"><i className="fa fa-newspaper" />News</div>
                        <ul className="ir-bullets">
                          {news.map((item, i) => (
                            <li key={i}>
                              {item.headline}
                              {item.date && <span className="ir-note-when"> · {item.date}</span>}
                              {item.url && <SourceLink url={item.url} />}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {(registerRows.length > 0 || changeLines.length > 0 || openJobs.length > 0) && (
                  <div className="ir-insight-grid">
                    {registerRows.length > 0 && (
                      <div className="ir-block">
                        <div className="ir-block-title">
                          <i className="fa fa-landmark" />Company register
                          {imprintPage && <SourceLink url={imprintPage} label="Imprint" />}
                        </div>
                        <dl className="ir-register">
                          {registerRows.map((r) => (
                            <div key={r.label} className="ir-register-row">
                              <dt>{r.label}</dt>
                              <dd>{r.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                    {(changeLines.length > 0 || openJobs.length > 0) && (
                      <div className="ir-block">
                        <div className="ir-block-title">
                          <i className="fa fa-arrows-rotate" />On the website
                          {siteFacts?.crawledAt && <span className="ir-note-when">checked {formatShortDate(siteFacts.crawledAt)}</span>}
                        </div>
                        {changeLines.length > 0 && (
                          <>
                            <p className="ir-block-sub">
                              Since last scan{siteChanges?.since ? ` (${formatShortDate(siteChanges.since)})` : ''}
                            </p>
                            <ul className="ir-bullets">
                              {changeLines.map((c, i) => (
                                <li key={i}>
                                  <span className="ir-change-kind">{c.kind}</span> {c.text}
                                  {c.url && <SourceLink url={c.url} />}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {openJobs.length > 0 && (
                          <>
                            <p className="ir-block-sub">Open positions ({siteFacts.jobs.length})</p>
                            <ul className="ir-bullets">
                              {openJobs.map((j, i) => <li key={i}>{j}</li>)}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {ri.reasoning && (
                  <div>
                    <button className="ir-reasoning-toggle" onClick={() => setReasoningOpen(!reasoningOpen)}>
                      <i className={`fa fa-${reasoningOpen ? 'chevron-down' : 'chevron-right'}`} />
                      <span>AI Reasoning</span>
                    </button>
                    {reasoningOpen && (
                      <div className="ir-reasoning-content">{asText(ri.reasoning)}</div>
                    )}
                  </div>
                )}
              </section>
            )}

            <ContactMediaInsight userId={userId} contactId={row.id} />

            {/* ── Activity ── */}
            <section className="ir-section">
              <div className="ir-section-label"><i className="fa fa-handshake" />Activity</div>
              <div className="ir-flags">
                <span className={`ir-flag ${hasReply ? 'ir-flag--active' : ''}`}>
                  <i className={`fa ${hasReply ? 'fa-check-circle' : 'fa-circle'}`} />Replied
                </span>
                {replySubtype && replySubtype !== 'neutral' && (
                  <span className="ir-flag ir-flag--reply" style={{
                    color: REPLY_COLORS[replySubtype] || '#94a3b8',
                    background: `${REPLY_COLORS[replySubtype] || '#94a3b8'}12`,
                    borderColor: `${REPLY_COLORS[replySubtype] || '#94a3b8'}30`,
                  }}>
                    <i className="fa fa-comment-dots" />{REPLY_LABELS[replySubtype] || replySubtype}
                  </span>
                )}
                <span className={`ir-flag ${row.isDisqualified ? 'ir-flag--warn' : ''}`}>
                  <i className={`fa ${row.isDisqualified ? 'fa-ban' : 'fa-circle'}`} />Disqualified
                </span>
                <span className={`ir-flag ${row.activeWarmupFlow ? 'ir-flag--active' : ''}`}>
                  <i className={`fa ${row.activeWarmupFlow ? 'fa-check-circle' : 'fa-circle'}`} />In Flow
                </span>
                {row.globalOptOut && (
                  <span className="ir-flag ir-flag--warn">
                    <i className="fa fa-hand-paper" />Opt-Out
                  </span>
                )}
              </div>
            </section>

            {/* ── Scores (collapsed by default) ── */}
            <section className="ir-section">
              <button className="ir-reasoning-toggle" onClick={() => setScoresOpen(!scoresOpen)}>
                <i className={`fa fa-${scoresOpen ? 'chevron-down' : 'chevron-right'}`} />
                <span>Scores</span>
                <span className="ir-toggle-summary">
                  {leadScore != null && `Lead ${leadScore}`}
                  {leadScore != null && spyConf != null && ' · '}
                  {spyConf != null && `Confidence ${spyConf}%`}
                </span>
              </button>
              {scoresOpen && (
                <div className="ir-meters ir-meters--open">
                  <ScoreMeter label="Lead Score" value={leadScore} max={100} icon="fa-star"
                    format={v => v} color={leadScore >= 60 ? '#22c55e' : leadScore >= 40 ? '#f59e0b' : '#94a3b8'} />
                  <ScoreMeter label="Enthusiasm" value={enthusiasm} max={10} icon="fa-fire"
                    format={v => v} color={enthusiasm >= 7 ? '#22c55e' : enthusiasm >= 4 ? '#f59e0b' : '#94a3b8'} />
                  <ScoreMeter label="Confidence" value={spyConf} max={100} icon="fa-check-double"
                    format={v => `${v}%`} color={spyConf >= 70 ? '#3b82f6' : spyConf >= 40 ? '#818cf8' : '#94a3b8'} />
                  {sourceQuality != null && (
                    <ScoreMeter label="Data Quality" value={sourceQuality} max={100} icon="fa-database"
                      format={v => v} color={sourceQuality >= 60 ? '#22c55e' : sourceQuality >= 30 ? '#f59e0b' : '#94a3b8'} />
                  )}
                </div>
              )}
            </section>

            {/* ── External Data: one tab per custom researcher ── */}
            <ConnectorDataInsight connectors={ri.connectors} />

            {/* ── Footer meta ── */}
            <div className="ir-meta">
              {hasResearch && ri.researchedAt && (
                <span className="ir-meta-item">
                  <i className="fa fa-clock" />Researched {new Date(ri.researchedAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
              {signals.computedAt && (
                <span className="ir-meta-item">
                  <i className="fa fa-sync-alt" />Analyzed {new Date(signals.computedAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>

          <aside className="ir-side">
            {/* ── Newest Signals ── */}
            {newestSignals.length > 0 && (
              <section className="ir-section">
                <div className="ir-section-label">
                  <i className="fa fa-bolt" />Newest Signals
                  {ri.newestSignalsAt && (
                    <span className="ir-section-count">{formatShortDate(ri.newestSignalsAt)}</span>
                  )}
                </div>
                <div className="ir-ns-list">
                  {newestSignals.map((s, i) => {
                    const type = NEWEST_SIGNAL_TYPES[s.type] || NEWEST_SIGNAL_TYPES.unique;
                    return (
                      <article key={i} className={`ir-ns-card ir-ns-card--${s.type in NEWEST_SIGNAL_TYPES ? s.type : 'unique'}`}>
                        <div className="ir-ns-top">
                          <span className="ir-ns-type"><i className={`fa ${type.icon}`} />{type.label}</span>
                          {s.isNew && <span className="ir-ns-new">New</span>}
                          {s.date && <span className="ir-ns-date">{formatShortDate(s.date)}</span>}
                        </div>
                        <div className="ir-ns-title">{asText(s.title)}</div>
                        {s.detail && <p className="ir-ns-detail">{asText(s.detail)}</p>}
                        {s.salesAngle && (
                          <p className="ir-ns-angle"><i className="fa fa-bullseye" />{asText(s.salesAngle)}</p>
                        )}
                        {safeUrl(s.sourceUrl) && (
                          <a className="ir-ns-source" href={safeUrl(s.sourceUrl)} target="_blank" rel="noopener noreferrer">
                            <i className="fa fa-link" />{displayHost(s.sourceUrl)}
                          </a>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Signals ── */}
            {hasSignals ? (
              <section className="ir-section">
                <div className="ir-section-label"><i className="fa fa-wave-square" />Signals</div>
                <div className="ir-signal-grid">
                  {signalFields.map((s, i) => (
                    <div key={i} className="ir-signal-card">
                      <div className="ir-signal-header">
                        <span className="ir-signal-label">{s.label}</span>
                        <ProvenanceBadge source={s.prov} />
                      </div>
                      <span className="ir-signal-value">{asText(s.value)}</span>
                    </div>
                  ))}
                </div>
              </section>
            ) : (
              <div className="ir-empty">
                <i className="fa fa-satellite-dish" />
                <span>No signal data yet. Signals are captured automatically when this contact enters your view.</span>
              </div>
            )}

            <GoogleReviewsInsight audit={ri.googleReviewAudit} />

            {/* ── AI Tags ── */}
            {tags.length > 0 && (
              <section className="ir-section">
                <div className="ir-section-label"><i className="fa fa-tags" />AI Tags</div>
                <div className="ir-tags">{tags.map((t, i) => <span key={i} className="ir-tag">{asText(t)}</span>)}</div>
              </section>
            )}

            {hasPotential && (
              <section className="ir-section">
                <div className="ir-section-label">
                  <i className="fa fa-bullseye" />Potential
                  {potentialScore != null && <span className="ir-section-count">{potentialScore}/10</span>}
                </div>
                <div className="ir-potential">
                  {(potential?.fitLabel || potential?.dataConfidence === 'none') && (
                    <span className="ir-potential-fit">
                      {potential?.dataConfidence === 'none' ? 'No public info' : asText(potential.fitLabel)}
                    </span>
                  )}
                  {potential?.summary && <p className="ir-prose">{asText(potential.summary)}</p>}
                  {potentialBullets.length > 0 && (
                    <ul className="ir-bullets">
                      {potentialBullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                    </ul>
                  )}
                  {potential?.analyzedAt && (
                    <div className="ir-seo-footer">
                      <i className="fa fa-clock" />
                      Analyzed {new Date(potential.analyzedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ── Instagram Insight ── */}
            {igInsight && (
              <section className="ir-section">
                <div className="ir-section-label">
                  <i className="fab fa-instagram" />Instagram
                  {igInsight.isActive !== undefined && (
                    <span className={`ir-ig-activity ${igInsight.isActive ? 'ir-ig-activity--on' : igInsight.postedInLast30Days ? 'ir-ig-activity--30d' : 'ir-ig-activity--off'}`}>
                      <i className={`fa ${igInsight.isActive ? 'fa-circle' : igInsight.postedInLast30Days ? 'fa-calendar-check' : 'fa-moon'}`} />
                      {igInsight.isActive
                        ? `Active${igInsight.daysSinceLastPost != null ? ` (${igInsight.daysSinceLastPost}d)` : ''}`
                        : igInsight.postedInLast30Days
                          ? `30d active${igInsight.daysSinceLastPost != null ? ` (${igInsight.daysSinceLastPost}d)` : ''}`
                          : `Inactive${igInsight.daysSinceLastPost != null ? ` (${igInsight.daysSinceLastPost}d)` : ''}`}
                    </span>
                  )}
                </div>
                <div className="ir-ig-card">
                  <p className="ir-ig-summary">{asText(igInsight.summary)}</p>
                  {igInsight.uniqueFacts?.length > 0 && (
                    <div className="ir-ig-detail">
                      <span className="ir-ig-detail-label"><i className="fa fa-star" />Key Facts</span>
                      <span className="ir-ig-detail-text">{igInsight.uniqueFacts.map(asText).join(' · ')}</span>
                    </div>
                  )}
                  {igInsight.topics?.length > 0 && (
                    <div className="ir-ig-topics">
                      {igInsight.topics.map((t, i) => <span key={i} className="ir-tag">{asText(t)}</span>)}
                    </div>
                  )}
                  <div className="ir-ig-meta">
                    {igInsight.activityScore != null && (
                      <span className={`ir-ig-badge ir-ig-score ir-ig-score--${igInsight.activityScore >= 7 ? 'high' : igInsight.activityScore >= 4 ? 'mid' : 'low'}`} title="Activity">
                        <i className="fa fa-bolt" />{igInsight.activityScore}/10
                      </span>
                    )}
                    {igInsight.engagementScore != null && (
                      <span className={`ir-ig-badge ir-ig-score ir-ig-score--${igInsight.engagementScore >= 7 ? 'high' : igInsight.engagementScore >= 4 ? 'mid' : 'low'}`} title="Engagement">
                        <i className="fa fa-fire" />{igInsight.engagementScore}/10
                      </span>
                    )}
                    {igInsight.isDeadAccount && (
                      <span className="ir-ig-badge ir-ig-dead"><i className="fa fa-skull-crossbones" />dead</span>
                    )}
                    {igInsight.tone && <span className="ir-ig-badge"><i className="fa fa-comment-alt" />{asText(igInsight.tone)}</span>}
                    {igInsight.avgPostsPerWeek != null && <span className="ir-ig-badge"><i className="fa fa-calendar-alt" />{igInsight.avgPostsPerWeek}/week</span>}
                    {igInsight.avgLikes != null && <span className="ir-ig-badge"><i className="fa fa-heart" />~{igInsight.avgLikes} avg</span>}
                    {igInsight.language && <span className="ir-ig-badge"><i className="fa fa-globe" />{asText(igInsight.language)}</span>}
                    {igInsight.engagementTrend && igInsight.engagementTrend !== 'stable' && (
                      <span className={`ir-ig-badge ir-ig-trend--${igInsight.engagementTrend}`}>
                        <i className={`fa fa-arrow-${igInsight.engagementTrend === 'growing' ? 'up' : 'down'}`} />
                        {igInsight.engagementTrend}
                      </span>
                    )}
                  </div>
                  {igInsight.targetAudience && (
                    <div className="ir-ig-detail">
                      <span className="ir-ig-detail-label"><i className="fa fa-crosshairs" />Target</span>
                      <span className="ir-ig-detail-text">{asText(igInsight.targetAudience)}</span>
                    </div>
                  )}
                  {igInsight.painPoints?.length > 0 && (
                    <div className="ir-ig-detail">
                      <span className="ir-ig-detail-label"><i className="fa fa-exclamation-triangle" />Pain Points</span>
                      <span className="ir-ig-detail-text">{igInsight.painPoints.map(asText).join(' · ')}</span>
                    </div>
                  )}
                  {igInsight.sellingPoints?.length > 0 && (
                    <div className="ir-ig-detail">
                      <span className="ir-ig-detail-label"><i className="fa fa-gem" />USPs</span>
                      <span className="ir-ig-detail-text">{igInsight.sellingPoints.map(asText).join(' · ')}</span>
                    </div>
                  )}
                  {igInsight.salesAngle && (
                    <div className="ir-ig-sales-angle">
                      <i className="fa fa-lightbulb" />
                      <span>{asText(igInsight.salesAngle)}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            <WebsiteAnalyzerInsight analysis={row.websiteAnalysis} scannedAt={row.website_analysis_at} />

            {/* ── SEO Analyzer (Sistrix) ── */}
            {seo && seo.data && (
              <section className="ir-section">
                <div className="ir-section-label">
                  <i className="fa fa-magnifying-glass-chart" />SEO Analyzer
                  {seo.country && <span className="ir-section-count">{String(seo.country).toUpperCase()}</span>}
                </div>
                <div className="ir-seo-grid">
                  {seo.data.core?.visibility?.value != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Visibility</span>
                      <span className="ir-seo-value">{Number(seo.data.core.visibility.value).toFixed(2)}</span>
                    </div>
                  )}
                  {seo.data.core?.keywordsTotal != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Keywords</span>
                      <span className="ir-seo-value">{Number(seo.data.core.keywordsTotal).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {seo.data.core?.keywordsTop10 != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Top 10</span>
                      <span className="ir-seo-value">{Number(seo.data.core.keywordsTop10).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {seo.data.trafficValue?.estimatedTrafficMonthly != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Visitors/mo</span>
                      <span className="ir-seo-value">~{Number(seo.data.trafficValue.estimatedTrafficMonthly).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {seo.data.trafficValue?.estimatedValueMonthly ? (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Traffic Value</span>
                      <span className="ir-seo-value">~{Number(seo.data.trafficValue.estimatedValueMonthly).toLocaleString('de-DE')} €</span>
                    </div>
                  ) : null}
                  {seo.data.backlinks?.total != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Backlinks</span>
                      <span className="ir-seo-value">{Number(seo.data.backlinks.total).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {seo.data.visibilityTrend?.changePct != null && (
                    <div className="ir-seo-metric">
                      <span className="ir-seo-label">Trend {seo.data.visibilityTrend.overWindow || '12M'}</span>
                      <span
                        className="ir-seo-value"
                        style={{ color: seo.data.visibilityTrend.direction === 'down' ? '#dc2626' : seo.data.visibilityTrend.direction === 'up' ? '#16a34a' : undefined }}
                      >
                        {seo.data.visibilityTrend.changePct > 0 ? '+' : ''}{seo.data.visibilityTrend.changePct}%
                      </span>
                    </div>
                  )}
                </div>
                {Array.isArray(seo.data.competitors) && seo.data.competitors[0] && (
                  <div className="ir-seo-row">
                    <span className="ir-seo-row-label"><i className="fa fa-users" />Top Competitor</span>
                    <a href={`https://${seo.data.competitors[0].domain}`} target="_blank" rel="noopener noreferrer" className="ir-seo-row-value">
                      {seo.data.competitors[0].domain}
                    </a>
                  </div>
                )}
                {Array.isArray(seo.data.opportunities) && seo.data.opportunities[0] && (
                  <div className="ir-seo-row">
                    <span className="ir-seo-row-label"><i className="fa fa-bullseye" />Top Chance</span>
                    <span className="ir-seo-row-value">
                      "{seo.data.opportunities[0].keyword}" · Pos {seo.data.opportunities[0].position}
                    </span>
                  </div>
                )}
                {seo.scannedAt && (
                  <div className="ir-seo-footer">
                    <i className="fa fa-clock" />
                    Scanned {new Date(seo.scannedAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {seo.sistrixCreditsUsed != null && ` · ${seo.sistrixCreditsUsed} credits`}
                  </div>
                )}
              </section>
            )}
          </aside>
        </div>
        {mailView && <RecordedMail view={mailView} onClose={() => setMailView(null)} />}
      </div>
    </div>
  );
};

export default IntelligenceModal;
